import {
  PurchaseAgentBudgetCell,
  PurchaseAgentDeactivateButton,
  PurchaseAgentLimitHeadCell,
  PurchaseAgentPrimaryStateButton,
  PurchaseAgentStateLabel,
  PurchaseAgentVehiclesCell,
} from '@components/purchase-agent-state';
import { HeadCell, Table, TextCell, TimestampCell } from '@components/ui';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import SearchIcon from '@material-ui/icons/Search';
import {
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChangePurchaseAgentState, useDeletePurchaseAgent, usePurchaseAgents } from 'src/hooks/usePurchaseAgents';
import useRole from 'src/hooks/useRole';
import { useUser } from 'src/hooks/useUser';
import { AuthenticationProvider, PurchaseAgentListItem } from 'src/modules/generated/api';
import { getStateActions } from 'src/modules/purchase-agent';
import { isSystemAgent, useSystemAgentLabel } from 'src/modules/system-agents';
import UserRole from 'src/types/UserRoles';

const NameWithStateCell = ({ row }: { row: PurchaseAgentListItem }) => (
  <TextCell
    text={<strong>{row.name}</strong>}
    supportText={<PurchaseAgentStateLabel state={row.state} />}
    startAdornment={<PurchaseAgentPrimaryStateButton agent={row} />}
  />
);

const DeleteCell = ({ row }: { row: PurchaseAgentListItem }) => {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { mutate: deletePurchaseAgent, isLoading: isDeleting } = useDeletePurchaseAgent();
  const { mutate: changeState } = useChangePurchaseAgentState();
  const { canDeactivate } = getStateActions(row.state);

  const handleConfirm = () => {
    const id = row.id?.purchaseAgentId;
    if (id) {
      deletePurchaseAgent(id, { onSettled: () => setConfirmOpen(false) });
    } else {
      setConfirmOpen(false);
    }
  };

  const handleDeactivate = () => {
    const id = row.id?.purchaseAgentId;
    if (id) {
      changeState({ id, action: 'deactivate' }, { onSuccess: () => setConfirmOpen(false) });
    } else {
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <Tooltip title={t('purchaseAgent.delete.tooltip')} placement="top">
        <IconButton size="small" onClick={() => setConfirmOpen(true)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{t('purchaseAgent.delete.confirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('purchaseAgent.delete.confirmText')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="secondary" variant="outlined" size="small">
            {t('common.cancel')}
          </Button>
          {canDeactivate && (
            <Button onClick={handleDeactivate} color="secondary" variant="contained" size="small">
              {t('purchaseAgent.deactivate.tooltip')}
            </Button>
          )}
          <Button onClick={handleConfirm} color="primary" variant="contained" size="small" disabled={isDeleting}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const EditCell = ({ onEdit }: { onEdit: () => void }) => {
  const { t } = useTranslation();
  return (
    <Tooltip title={t('common.edit')} placement="top">
      <IconButton size="small" onClick={onEdit}>
        <EditIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const buildColumns = (
  onEdit: (id: string) => void,
  getSearchAgentLabel: (name: string) => string,
  authProvider: string,
  userId: string,
  isAdmin: boolean,
  currentUserId: string | undefined,
  currentProvider: AuthenticationProvider | undefined,
): ColumnDef<PurchaseAgentListItem>[] => [
  {
    id: 'name',
    accessorFn: (pa) => pa.name ?? '',
    header: ({ column }) => (
      <HeadCell
        text="purchaseAgent.list.columns.name"
        sortDir={column.getIsSorted()}
        onSort={() => column.toggleSorting()}
      />
    ),
    cell: ({ row }) => <NameWithStateCell row={row.original} />,
  },
  {
    id: 'creator',
    accessorFn: (pa) => pa.created?.by?.name ?? '',
    header: ({ column }) => (
      <HeadCell
        text="purchaseAgent.list.columns.creator"
        sortDir={column.getIsSorted()}
        onSort={() => column.toggleSorting()}
      />
    ),
    cell: ({ row }) => <TextCell text={row.original.created?.by?.name} />,
  },
  {
    id: 'linkedSearchAgent',
    accessorFn: (pa) => getSearchAgentLabel(pa.linkedSearchAgent?.name ?? ''),
    header: ({ column }) => (
      <HeadCell
        text="purchaseAgent.list.columns.linkedSearchAgent"
        sortDir={column.getIsSorted()}
        onSort={() => column.toggleSorting()}
      />
    ),
    cell: ({ row }) => {
      const { linkedSearchAgent } = row.original ?? {};
      if (!linkedSearchAgent?.name) return <TextCell text={undefined} />;
      const label = getSearchAgentLabel(linkedSearchAgent.name);
      if (isSystemAgent(linkedSearchAgent.name, authProvider, userId)) return <TextCell text={label} />;
      return <MuiLink href={`/cars?searchagent=${linkedSearchAgent.id?.value}`}>{label}</MuiLink>;
    },
  },
  {
    id: 'budget',
    enableSorting: false,
    enableGlobalFilter: false,
    header: () => <PurchaseAgentLimitHeadCell text="purchaseAgent.list.columns.budget" />,
    cell: ({ row }) => <PurchaseAgentBudgetCell agent={row.original} />,
  },
  {
    id: 'vehicles',
    enableSorting: false,
    enableGlobalFilter: false,
    header: () => <PurchaseAgentLimitHeadCell text="purchaseAgent.list.columns.vehicles" />,
    cell: ({ row }) => <PurchaseAgentVehiclesCell agent={row.original} />,
  },
  {
    id: 'createdAt',
    accessorFn: (pa) => pa.created?.at ?? '',
    header: ({ column }) => (
      <HeadCell
        text="purchaseAgent.list.columns.createdAt"
        sortDir={column.getIsSorted()}
        onSort={() => column.toggleSorting()}
      />
    ),
    cell: ({ row }) => <TimestampCell timestamp={row.original.created?.at} />,
  },
  {
    id: 'actions',
    enableSorting: false,
    enableGlobalFilter: false,
    header: () => null,
    cell: ({ row }) => {
      const id = row.original.id?.purchaseAgentId;
      const isOwner =
        row.original.created?.by?.id === currentUserId &&
        row.original.created?.by?.authenticationProvider === currentProvider;
      const canEdit = isOwner;
      const canDelete = isAdmin || isOwner;
      return (
        <>
          <PurchaseAgentDeactivateButton agent={row.original} />
          {id && canEdit && <EditCell onEdit={() => onEdit(id)} />}
          {canDelete && <DeleteCell row={row.original} />}
        </>
      );
    },
  },
];

type PurchaseAgentTableProps = {
  className?: string;
  onEditPurchaseAgent: (id: string) => void;
};

export const PurchaseAgentTable = ({ className, onEditPurchaseAgent }: PurchaseAgentTableProps): JSX.Element => {
  const { t } = useTranslation();
  const { data: user } = useUser();
  const { hasRole } = useRole();
  const isAdmin = hasRole(UserRole.ADMIN);
  const {
    data: purchaseAgents,
    isLoading: isPurchaseAgentsLoading,
    isError: isPurchaseAgentsError,
  } = usePurchaseAgents();

  const authProvider = user?.user?.authenticationProvider ?? '';
  const userId = user?.user?.id ?? '';
  const getSearchAgentLabel = useSystemAgentLabel();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [inputValue, setInputValue] = useState('');
  const [globalFilter, setGlobalFilter] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setGlobalFilter(inputValue), 250);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const data = useMemo(() => purchaseAgents ?? [], [purchaseAgents]);

  const columns = useMemo(
    () =>
      buildColumns(
        onEditPurchaseAgent,
        getSearchAgentLabel,
        authProvider,
        userId,
        isAdmin,
        user?.user?.id,
        user?.user?.authenticationProvider,
      ),
    [
      onEditPurchaseAgent,
      getSearchAgentLabel,
      authProvider,
      userId,
      isAdmin,
      user?.user?.id,
      user?.user?.authenticationProvider,
    ],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    state: { sorting, globalFilter },
    getRowId: (pa, index) => pa.id?.purchaseAgentId ?? String(index),
  });

  if (isPurchaseAgentsLoading) {
    return (
      <Grid container justifyContent="center" style={{ paddingBlock: 32 }}>
        <CircularProgress />
      </Grid>
    );
  }

  if (isPurchaseAgentsError) {
    return <Typography color="error">{t('purchaseAgent.list.loadError')}</Typography>;
  }

  return (
    <div className={className}>
      <TextField
        size="small"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder={t('filterSearch.search')}
        variant="outlined"
        fullWidth
        style={{ marginBottom: 8 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: inputValue ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setInputValue('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <Table table={table as any} />
    </div>
  );
};
